import { downloadFile } from "@f2e/shared/browser/download"

import { API_PREFIX } from "@/constants"
import type { FileBadgeData } from "@/types"

const shouldUseConfiguredServerRoot = (url: URL): boolean => {
  return (
    url.pathname.startsWith(API_PREFIX) || url.pathname.startsWith("/media/")
  )
}

const KB_UPLOAD_URL_PATTERN = /^kb-upload:\/\/([1-9]\d*)$/

export interface AttachmentResourceScope {
  organizationId?: string
  workspaceId?: string
}

export const resolveAttachmentResourceUrl = (
  url: string,
  serverRoot: string,
  scope?: AttachmentResourceScope
): string => {
  const kbUploadMatch = url.match(KB_UPLOAD_URL_PATTERN)
  if (kbUploadMatch) {
    if (!scope?.organizationId || !scope.workspaceId) return url

    const path = `${API_PREFIX}/organizations/${encodeURIComponent(scope.organizationId)}/workspaces/${encodeURIComponent(scope.workspaceId)}/knowledge_base_uploads/${kbUploadMatch[1]}/media`
    return new URL(path, serverRoot).toString()
  }

  try {
    const resolvedUrl = new URL(url, serverRoot)
    const configuredServerRoot = new URL(serverRoot)

    if (
      resolvedUrl.origin !== configuredServerRoot.origin &&
      shouldUseConfiguredServerRoot(resolvedUrl)
    ) {
      return new URL(
        `${resolvedUrl.pathname}${resolvedUrl.search}${resolvedUrl.hash}`,
        configuredServerRoot
      ).toString()
    }

    return resolvedUrl.toString()
  } catch {
    return url
  }
}

export const downloadAttachmentResource = async (
  file: Pick<FileBadgeData, "url" | "name">,
  serverRoot: string
): Promise<boolean> => {
  if (!file.url) return false

  const resolvedUrl = resolveAttachmentResourceUrl(file.url, serverRoot)

  try {
    new URL(resolvedUrl)
  } catch {
    return false
  }

  return downloadFile(resolvedUrl, file.name, {
    credentials: "include",
  })
}

export const loadAttachmentPreviewResource = async (
  file: Pick<FileBadgeData, "url" | "name">,
  serverRoot: string
): Promise<string> => {
  if (!file.url) {
    throw new Error("Attachment preview URL is missing")
  }

  const resolvedUrl = resolveAttachmentResourceUrl(file.url, serverRoot)
  const response = await fetch(resolvedUrl, {
    method: "GET",
    credentials: "include",
  })

  if (!response.ok) {
    throw new Error(`Attachment preview failed (${response.status})`)
  }

  return window.URL.createObjectURL(await response.blob())
}
